import React from 'react';
import { FormField } from 'ui/formField';
import { Modal, ModalFooter, ModalBody, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { PrimaryButton, BUTTON_SIZES } from 'ui/button';
import { LongArrowLeft } from 'ui/icons';
import { deepMerge } from 'utils/helpers';
import MarkdownPreviewPanel from './MarkdownPreviewPanel';
import { SettingsPanel } from './SettingsPanel';
import VisualizationPanel from './VisualizationPanel';
import WidgetTypeDropdown from './WidgetTypeDropdown';
import * as widgetTypes from '../../utils/dashboardWidgetTypes';
import {
  getWidgetFetchParams,
  getWidgetMetric,
  getWidgetSettings,
  getWidgetType,
  getWidgetVisualization,
} from '../../utils/dashboardWidgetUtils';
import { getWidgetSettingsForVisualization } from '../../utils/visualizations';
import {
  ContentContainer,
  SettingsPanelContainer,
  VisualizationsPanelContainer,
  SettingsMessageContainer,
} from './AddEditModal.styled';
import { shouldShowSettingsPanel } from './utils';

// type Props = {
//   /** Toggle add and edit mode. */
//   addMode: boolean,
//   /** Title of modal in add mode. */
//   addText: string,
//   /** List of metrics on the dashboard. */
//   metrics: [],
//   /** Title of modal in edit mode. */
//   editText: string,
//   /** Function to fetch campaigns. */
//   fetchCampaigns: ({}) => void,
//   /** Callback function when modal is closed. */
//   onClose: ({}) => void,
//   /** Callback function when submit is clicked to save widget. */
//   onSaveWidget: ({}) => void,
//   /** Determine if modal is open. */
//   open: boolean,
//   /** Currently selected widget in edit mode. */
//   selectedWidget: { originalWidget: {} },
// };

export default class AddEditModal extends React.Component {
  static defaultProps = {
    addMode: true,
    addText: 'New widget',
    addButtonText: 'Create widget',
    metrics: [],
    editText: 'Edit widget',
    editButtonText: 'Save widget',
    fetchCampaigns: () => {},
    onClose: () => {},
    onSaveWidget: () => {},
    selectedWidget: {
      settings: {},
      name: '',
    },
  };

  constructor(props) {
    super(props);
    this.state = {
      markdownHeight: 0,
      markdownScrollHeight: 0,
      markdownScrollTop: 0,
      selectedWidget: this.props.selectedWidget,
    };
  }

  /**
   * Callback function when modal closes.
   */
  handleClose = () => {
    this.props.onClose();
  };

  /**
   * Resets selectedWidget to original then closes modal.
   */
  resetClose = () => {
    this.handleChangeWidget({}, this.props.selectedWidget.originalWidget);
    this.props.onClose();
  };

  /**
   * Callback function when config is changed.
   *
   * ```
   * handleChangeSettings(settings)
   * ```
   * @param { Object } e: The change event.
   * @param { Object } settings: The updated settings.
   * @return { void }
   */
  handleChangeSettings = (e, settings) => {
    this.handleChangeWidget(e, { settings });
  };

  /**
   * Callback function when name is changed.
   *
   * ```
   * handleChangeName(e, name)
   * ```
   * @param { Object } e: The change event.
   * @param { String } name: The updated name.
   * @return { void }
   */
  handleChangeName = (e, name) => {
    this.handleChangeWidget(e, { name });
  };

  /**
   * Callback function when widget is changed.
   *
   * ```
   * handleChangeWidget(e, newWidgetProps)
   * ```
   * @param { Object } e: The change event.
   * @param { Object } newWidgetProps: The updated widget props.
   * @return { void }
   */
  handleChangeWidget = (e, newWidgetProps) => {
    const { selectedWidget } = this.state;
    this.setState({
      selectedWidget: deepMerge(
        {},
        Object.assign({}, selectedWidget),
        Object.assign({}, newWidgetProps),
      ),
    });
  };

  handleScrollMarkdown = (e) => {
    if (e && e.target) {
      this.setState({
        markdownHeight: e.target.offsetHeight,
        markdownScrollHeight: e.target.scrollHeight,
        markdownScrollTop: e.target.scrollTop,
      });
    }
  };

  /**
   * Callback function when submit button is clicked in modal.
   *
   * ```
   * handleSubmit()
   * ```
   * @return { void }
   */
  handleSubmit = () => {
    const { fetchCampaigns, onSaveWidget } = this.props;
    const { selectedWidget } = this.state;
    const widget = deepMerge({}, { type: widgetTypes.METRIC }, selectedWidget);
    const widgetOptions = {
      ...widget,
      id: selectedWidget.id,
    };
    if (getWidgetType(widget) === widgetTypes.CAMPAIGNS_TABLE) {
      fetchCampaigns({ fetchParams: getWidgetFetchParams(widget), widget });
    }
    onSaveWidget(widgetOptions);
    this.handleClose();
  };

  showSettingsPanel = shouldShowSettingsPanel;

  render() {
    const { addMode, addText, addButtonText, editText, editButtonText, metrics } = this.props;
    const { markdownHeight, markdownScrollHeight, markdownScrollTop, selectedWidget } = this.state;
    const name = selectedWidget?.name || '';
    const type = getWidgetType(selectedWidget);
    const isMetricWidget = type === widgetTypes.METRIC;
    const isMarkdownWidget = type === widgetTypes.MARKDOWN;
    const settings = getWidgetSettings(selectedWidget);
    const selectedMetric = getWidgetMetric(selectedWidget);
    const selectedVisualization = getWidgetVisualization(selectedWidget);
    const showSettingsPanel = this.showSettingsPanel(type, selectedMetric, selectedVisualization);
    const { settingsList } = getWidgetSettingsForVisualization(selectedVisualization);

    const visualizationsPanel = (
      <div>
        <FormField labelText="Widget type">
          <WidgetTypeDropdown onChange={this.handleChangeWidget} widgetType={type} />
        </FormField>
        {isMetricWidget && (
          <VisualizationPanel
            metrics={metrics}
            onChange={this.handleChangeWidget}
            selectedMetric={selectedMetric}
            selectedVisualization={selectedVisualization}
          />
        )}
        {isMarkdownWidget && (
          <MarkdownPreviewPanel
            offsetHeight={markdownHeight}
            scrollHeight={markdownScrollHeight}
            scrollTop={markdownScrollTop}
            text={settings.text}
          />
        )}
      </div>
    );

    const settingsPanel = showSettingsPanel ? (
      <SettingsPanel
        metric={selectedMetric}
        name={name}
        onChangeSettings={this.handleChangeSettings}
        onChangeName={this.handleChangeName}
        onScrollMarkdown={this.handleScrollMarkdown}
        settings={settings}
        settingsList={settingsList}
      />
    ) : (
      <SettingsMessageContainer>
        <LongArrowLeft />
        Select a metric and chart type
      </SettingsMessageContainer>
    );

    return (
      <Modal
        id="add-edit-widget-modal"
        onClose={this.resetClose}
        size={MODAL_SIZES.LARGE}
        variant={MODAL_VARIANTS.DEFAULT}
      >
        <ModalHeader>{addMode ? addText : editText}</ModalHeader>
        <ModalBody>
          <ContentContainer>
            <VisualizationsPanelContainer>{visualizationsPanel}</VisualizationsPanelContainer>
            <SettingsPanelContainer>{settingsPanel}</SettingsPanelContainer>
          </ContentContainer>
        </ModalBody>
        <ModalFooter>
          <PrimaryButton
            key="save"
            disabled={!showSettingsPanel}
            onClick={this.handleSubmit}
            size={BUTTON_SIZES.LARGE}
          >
            {addMode ? addButtonText : editButtonText}
          </PrimaryButton>
        </ModalFooter>
      </Modal>
    );
  }
}
